if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      updateUpsellFromHtml(htmlDoc) {
        const base = this.sectionId;
        const alt  = this.dataset.section; 

        const selectors = [
          `#Upsell-${base}`,
          `#Upsell-${base}-desktop`,
          `#Upsell-${base}-mobile`,
          `#Upsell-${alt}`,
          `#Upsell-${alt}-desktop`,
          `#Upsell-${alt}-mobile`,
        ];

        const freshScope = htmlDoc.querySelector(`#shopify-section-${base}`) || htmlDoc;

        selectors.forEach((sel) => {
          const src = freshScope.querySelector(sel);
          if (!src) return;

          const dests = document.querySelectorAll(sel);
          if (!dests.length) return;

          dests.forEach((dst) => {
            dst.innerHTML = src.innerHTML;
          });
        });

        this.reinitProductFormAndButtons(this);

        setTimeout(() => {
          if (window.initAllUpsell) window.initAllUpsell();
        }, 0);
      }


      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const needCount = this.requiredOptionCount;
        const haveCount = Array.isArray(selectedOptionValues) ? selectedOptionValues.length : 0;

        if (needCount === 0 || haveCount < needCount) {
          this.updateURL(this.dataset.url || (target && target.dataset?.productUrl) || '', null);
          this.updateVariantInputs(null);
          this.setUnavailable();
          try {
            const selectedLabel = this.querySelector('variant-selects [data-selected-value]');
            if (selectedLabel) selectedLabel.textContent = '';
          } catch (e) {}
          return; 
        }

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }


      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
          this.updateUpsellFromHtml(html);

          this.reinitProductFormAndButtons(this);


          this.updateCustomShopperReview(variant);
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const el = productInfoNode?.querySelector('variant-selects [data-selected-variant]');
        const json = el ? el.textContent.trim() : '';
        return json ? JSON.parse(json) : null;
      }
      
      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        if (!shouldFetchFullPage) {
          params.push(`section_id=${this.sectionId}`);
          params.push(`sections_url=${encodeURIComponent(window.location.pathname)}`);
        }
        if (optionValues && optionValues.length) {
          params.push(`option_values=${encodeURIComponent(optionValues.join(','))}`);
        }
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const freshScope = html.querySelector(`#shopify-section-${this.sectionId}`) || html;
        const newVariantSelects = freshScope.querySelector('variant-selects');
        const currentVariantSelects = this.querySelector('variant-selects');
        if (!newVariantSelects || !currentVariantSelects) return;

        currentVariantSelects.replaceWith(newVariantSelects);

        this.reinitProductFormAndButtons(this);
      }

      reinitProductFormAndButtons(root = this) {
        if (typeof ProductForm !== 'undefined') {
          root.querySelectorAll('product-form').forEach((form) => {
            new ProductForm(form);
          });
        }
        try { window?.Shopify?.PaymentButton?.init(); } catch (e) {}
        try { window?.ProductModel?.loadShopifyXR(); } catch (e) {}
      }

      updateCustomShopperReview(variant) {
        const reviewElements = document.querySelectorAll('.custom-shopper-review');
        let stickyVariant = document.querySelector('sticky-atc-bar .product-variant-id');
        let mainGallery = document.querySelector('.product__media-wrapper');
        let bigMediaPDPs = document.querySelectorAll('.parent-media_custom');
        if (stickyVariant && variant?.id) {
          stickyVariant.setAttribute('value', variant.id);
          stickyVariant.value = variant.id;
        }

        let isQuickAdd = this.id.includes("quickadd");
        const customMetafieldsScript = document.querySelector(
          'variant-selects [data-selected-variant-metafields]'
        );
        let dataAmount = '';
        let isFreeShipping = false;
        if (customMetafieldsScript && customMetafieldsScript.textContent.trim() !== '') {
          try {
            const metafields = JSON.parse(customMetafieldsScript.textContent);
            dataAmount = metafields?.amount_in_case || '';
            if (dataAmount.trim() !== '') {
              dataAmount = dataAmount.replace(/case/gi, '').trim();
            }
            isFreeShipping = metafields?.free_shipping_status || false;
            if(isFreeShipping == true){
              bigMediaPDPs.forEach(el =>{
                el.classList.add('is_freeshipping')
              })
            }else{
              bigMediaPDPs.forEach(el =>{

                el.classList.remove('is_freeshipping')
              })
            }
          } catch (err) {
            console.error('Failed parse metafields:', err);
          }
        }

        const priceUSD = (variant.price / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD' 
        });

        if (!isQuickAdd) {
          let titleProduct = document.querySelector('.product__title h1');
          let stickyPrice = document.querySelector('.sticky-atc-bar__product-info .price-item.price-item--regular');
          let stickyTitle = document.querySelector('.sticky-atc-bar__product-title');

          // console.log("stickyPrice", stickyPrice)

          if (variant) {
            if (dataAmount !== '' && variant.option2 !== 'Single') {
              if (titleProduct && stickyTitle) {
                titleProduct.textContent = `${variant.name} ${dataAmount}`;
                stickyTitle.textContent = `${variant.name} ${dataAmount}`;
                stickyPrice.textContent = `${priceUSD} ${dataAmount}`
              }
            } else {
              if (titleProduct && stickyTitle) {
                titleProduct.textContent = `${variant.name}`;
                stickyTitle.textContent = `${variant.name}`;
                stickyPrice.textContent = `${priceUSD}`
              }
            }
          }

          // Update variant specifications in table
          let tableSize = document.querySelector('[data-variant-spec="Size"]');
          let tableUnit = document.querySelector('[data-variant-spec="Unit"]');
          let tableSKu = document.querySelector('[data-variant-spec="SKU"]');

          if (tableSKu && variant?.sku) tableSKu.innerHTML = `${variant.sku}`;
          if (tableSize && variant?.option1) tableSize.innerHTML = `${variant.option1}`;
          if (tableUnit && variant?.option2) tableUnit.innerHTML = `${variant.option2}`;

          // Reset stars wrapper if empty
          let starsWrapper = document.querySelector('#product_just_stars');
          if (starsWrapper && starsWrapper.children.length < 1) {
            starsWrapper.innerHTML = `
              <span class="off"></span>
              <span class="off"></span>
              <span class="off"></span>
              <span class="off"></span>
              <span class="off"></span>
              <span class="ind_cnt low">
                <a class="sa_jump_to_reviews" href="#review_header">0 <span class="ind_cnt_desc">reviews</span></a>
              </span>
            `;
          }

          if (mainGallery && variant) {
            mainGallery.classList.remove('isInitialized');
            mainGallery.dispatchEvent(new CustomEvent('media-changed', {
              detail: { variant: variant, sku: variant?.sku },
              bubbles: true
            }));
          }
        } else {
          let quickAddModal = document.querySelector('quick-add-modal[open] .quick-add-modal__content-info');
          if (quickAddModal) {
            let productVariantId = quickAddModal.querySelector('.product-variant-id');
            if (productVariantId && variant?.id) {
              productVariantId.value = variant.id;
            }
          }
        }

        if (reviewElements && reviewElements.length > 0 && variant) {
          reviewElements.forEach(reviewElement => {
            let processedSku = variant.sku || '';
            if (processedSku.startsWith('Z-')) processedSku = processedSku.substring(2);
            reviewElement.setAttribute('data-variant', processedSku);

            if (typeof reviewElement.refresh === 'function') reviewElement.refresh(variant.id);
            if (typeof reviewElement.updateVariant === 'function') reviewElement.updateVariant(variant);

            reviewElement.dispatchEvent(new CustomEvent('variant-changed', {
              detail: { variantId: variant.id, variant, processedSku },
              bubbles: true
            }));
          });
        }

        // Force reload ShopperApproved with new SKU
        if (variant?.sku && typeof loadShopperApproved === 'function') {
          let processedSku = variant.sku;
          setTimeout(() => { loadShopperApproved(processedSku); }, 100);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          // Re-init form & buttons setelah variant-selects diganti
          this.reinitProductFormAndButtons(this);

          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.updateCustomShopperReview(null);
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateUpsellFromHtml(html);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const isDisabled = html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? false;
          this.productForm?.toggleSubmitButton(isDisabled, isDisabled ? window.variantStrings.soldOut : '');

          this.updateCustomShopperReview(variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, html, variant },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const gallery = this.querySelector('media-gallery');
        const newGallery = html.querySelector('media-gallery');
        if (!gallery || !newGallery) return;

        const ulSrc = gallery.querySelector('ul');
        const ulNew = newGallery.querySelector('ul');
        if (!ulSrc || !ulNew) return;

        const currentIds = Array.from(ulSrc.querySelectorAll('li[data-media-id]')).map(li => li.dataset.mediaId).join(',');
        const nextIds    = Array.from(ulNew.querySelectorAll('li[data-media-id]')).map(li => li.dataset.mediaId).join(',');

        this._lastFeaturedId = this._lastFeaturedId || null;
        const fullFeaturedId = `${this.dataset.section}-${variantFeaturedMediaId}`;

        if (currentIds === nextIds) {
          if (this._lastFeaturedId !== fullFeaturedId) {
            gallery.setActiveMedia?.(fullFeaturedId, true);
            this._lastFeaturedId = fullFeaturedId;
          }
          return;
        }

        const lockH = gallery.offsetHeight;
        gallery.style.minHeight = `${lockH}px`;

        const newChildren = Array.from(ulNew.children).map(node => node.cloneNode(true));
        ulSrc.replaceChildren(...newChildren);

        requestAnimationFrame(() => {
          gallery.setActiveMedia?.(fullFeaturedId, true);
          this._lastFeaturedId = fullFeaturedId;
          gallery.style.minHeight = '';
        });
      }


      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);

          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get requiredOptionCount() {
        const vs = this.querySelector('variant-selects');
        if (!vs) return 0;
        return vs.querySelectorAll('fieldset').length || 0;
      }


      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
